import { Group, Paper, Stack, Text } from '@mantine/core';
import { FC } from 'react';
import { ReactComponent as IconCalendar } from './img/iconCalendar.svg';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import { ReactComponent as IconTime } from './img/iconTime.svg';
import { ReactComponent as IconReject } from './img/iconReject.svg';
import { ReactComponent as IconCancel } from './img/iconCancel.svg';
import { ReactComponent as IconComment } from './img/iconCommentGray.svg';
import { ReactComponent as IconRuble } from './img/iconRuble.svg';
import { ReactComponent as IconOferta } from './img/iconOferta.svg';
import { ReactComponent as IconDoneBlue } from './img/iconDoneBlue.svg';
import { formatCustomDateContract, formatEndDateShort } from 'utils/date';
import { statusText } from 'components/ContractItem';
import { ContractActionsDrawer } from 'components/ContractItem/ContractActionsDrawer';
import { ContractStatus, ContractType } from 'redux/Api/contractsApi/type';
import styles from '../ContractsListMobile/ContractsListMobile.module.scss';
import { CommentTooltip } from 'components/ContractsListMobile';
import { FormatAmount } from 'ui/FormatAmount';
import { ContractActions } from 'components/ContractItem/ContractActions';
import { useMediaQuery } from '@mantine/hooks';

interface ContractsListMobile2Types {
  data: ContractType[];
  intersectionComponent: JSX.Element | null;
}

export const statusIcons: Record<ContractStatus, JSX.Element> = {
  signed_by_customer: <IconTime />,
  signed_by_worker: <IconDone />,
  rejected: <IconCancel />,
  revoked: <IconReject />,
  terminated: <IconReject />,
  expired: <IconReject />,
};

export const statusColor: Record<ContractStatus, string> = {
  signed_by_customer: '#121922',
  signed_by_worker: '#28A879',
  rejected: '#E10D0D',
  revoked: '#121922',
  terminated: '#121922',
  expired: '#121922',
};

export const ContractsListMobile2: FC<ContractsListMobile2Types> = ({
  data,
  intersectionComponent,
}) => {
  const isMobile = useMediaQuery(`(width < 768px)`);
  return (
    <Stack align='stretch' justify='center' mb='lg' gap='12px'>
      {data?.length ? (
        data.map((item) => (
          <Paper
            shadow='0'
            p='0 0 12px 0'
            key={item.id}
            radius={0}
            style={{ borderBottom: '1px solid #EAF0FA' }}>
            <Group justify='space-between' wrap='nowrap'>
              <Text fw='500' fz='16px' lh='22px' c='#121922'>
                № {item.number}
              </Text>
              {isMobile ? (
                <ContractActionsDrawer
                  contract={item}
                  status={item.status}
                  isResident={item?.worker?.nationality === 'Россия'}
                />
              ) : (
                <ContractActions
                  contract={item}
                  status={item.status}
                  isResident={item?.worker?.nationality === 'Россия'}
                />
              )}
            </Group>

            <div className={styles.lineBox}>
              <IconCalendar />
              <Text lh='18px' fz='16px' c='#121922'>
                {item.date_start && formatEndDateShort(item.date_start)} -{' '}
                {item.date_end ? formatEndDateShort(item.date_end) : 'Бессрочно'}
              </Text>
            </div>

            <div className={styles.lineBox}>
              <IconOferta />
              <div className={styles.textOferta}>
                {item.oferta?.title || 'Единый договор'}
              </div>
            </div>

            <div className={styles.lineBox}>
              <IconRuble />
              <div>Лимит </div>
              <div className={styles.amount}>
                {item.limit ? (
                  <FormatAmount
                    amount={String(item.limit)}
                    compact
                    stylePadding={false}
                  />
                ) : (
                  'не указан'
                )}
              </div>
            </div>

            <div className={styles.lineBox}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'space-between',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {statusIcons[item.status as ContractStatus]}
                  <Text
                    lh='18px'
                    fw='400'
                    fz='16px'
                    c={statusColor[item.status as ContractStatus]}>
                    {statusText[item.status as ContractStatus]}{' '}
                    {item.status === 'rejected' || item.status === 'terminated'
                      ? formatCustomDateContract(item.status_changed_at)
                      : null}
                  </Text>
                </div>
                {!item.is_mvd_notified &&
                  !item.is_mvd_notified_on_termination &&
                  item.comment && (
                    <div style={{ paddingRight: '8px' }}>
                      <CommentTooltip comment={item.comment}>
                        <IconComment style={{ cursor: 'pointer' }} />
                      </CommentTooltip>
                    </div>
                  )}
              </div>
            </div>

            {item.is_mvd_notified && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'space-between',
                  height: '20px',
                }}>
                <div style={{ display: 'flex', margin: '8px 0', gap: '8px' }}>
                  <IconDoneBlue />
                  <Text lh='18px' fz='16px' c='#002CFB'>
                    {'Уведомил МВД о заключении'}
                  </Text>
                </div>
                {item.is_mvd_notified &&
                  !item.is_mvd_notified_on_termination &&
                  item.comment && (
                    <div style={{ paddingRight: '8px' }}>
                      <CommentTooltip comment={item.comment}>
                        <IconComment style={{ cursor: 'pointer' }} />
                      </CommentTooltip>
                    </div>
                  )}
              </div>
            )}

            {item.is_mvd_notified_on_termination && item.comment && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'space-between',
                  height: '20px',
                }}>
                <div style={{ display: 'flex', margin: '8px 0', gap: '8px' }}>
                  <IconDoneBlue />
                  <Text lh='18px' fz='16px' c='#002CFB'>
                    {'Уведомил МВД о расторжении'}
                  </Text>
                </div>
                {item.is_mvd_notified_on_termination && (
                  <div style={{ paddingRight: '8px' }}>
                    <CommentTooltip comment={item.comment}>
                      <IconComment style={{ cursor: 'pointer' }} />
                    </CommentTooltip>
                  </div>
                )}
              </div>
            )}
          </Paper>
        ))
      ) : (
        <Text fz='16px' lh='22px' c='#71869D'>
          Ничего не найдено
        </Text>
      )}
      {intersectionComponent}
    </Stack>
  );
};
